import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface Props {
  /** "task", "issue", "headline", "metric" — used in the dialog's title. */
  noun: string
  /** What the person is about to lose, as plain sentences. */
  consequence: string
  /** Runs the delete. Throwing keeps the dialog's job to the caller (it shows its own banner). */
  onDelete: () => void | Promise<void>
  disabled?: boolean
  /** Extra classes for the trigger, so it can sit in a form's action row. */
  className?: string
}

/**
 * A permanent-delete control that cannot be hit by accident.
 *
 * The trigger is an ordinary quiet button; tapping it opens a dialog that says
 * what will be lost, with Cancel focused by default so Enter never deletes.
 * It is a dialog rather than an inline confirm because the desktop task and
 * headline rows keep their buttons on one no-wrap line, where a sentence of
 * explanation has nowhere to go.
 *
 * Archive is the everyday way to get rid of something and is always mentioned
 * as the alternative: it hides the item but keeps it, and can be undone.
 *
 * The database re-checks that the caller is an admin — this only hides the
 * button from everyone else.
 */
export default function DeleteButton({ noun, consequence, onDelete, disabled, className }: Props) {
  const [open, setOpen] = useState(false)
  const [working, setWorking] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    cancelRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !working) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, working])

  const confirm = async () => {
    setWorking(true)
    try {
      await onDelete()
    } finally {
      // On success the parent normally unmounts this; on failure the parent
      // shows the error, and closing here lets them see it.
      setWorking(false)
      setOpen(false)
    }
  }

  return (
    <>
      <button
        type="button"
        className={`btn btn--quiet btn--delete${className ? ` ${className}` : ''}`}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        Delete
      </button>

      {open &&
        createPortal(
          // React bubbles events through a portal to the component's parents, so
          // without this a click in the dialog would also toggle the task row it
          // was opened from.
          <div
            className="dialog-backdrop"
            onClick={(e) => {
              e.stopPropagation()
              if (e.target === e.currentTarget && !working) setOpen(false)
            }}
          >
            <div
              className="dialog"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="delete-dialog-title"
              aria-describedby="delete-dialog-body"
            >
              <h2 className="dialog__title" id="delete-dialog-title">
                Delete this {noun} for good?
              </h2>
              <p className="dialog__body" id="delete-dialog-body">
                {consequence}
              </p>
              <p className="dialog__body muted">
                Archive instead if you only want it out of the way — that keeps it and can be undone.
              </p>
              <div className="dialog__actions">
                <button
                  ref={cancelRef}
                  type="button"
                  className="btn btn--quiet"
                  disabled={working}
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--danger"
                  disabled={working}
                  onClick={() => void confirm()}
                >
                  {working ? 'Deleting…' : 'Delete forever'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
